/**
 * Component categories card (Settings -> Equipment): the configurable list
 * behind the asset Components tab. Add a category (e.g. Air Motor), say
 * what it fits (robot / KRC / the system directly), rename or retire it.
 * Core hierarchy types are protected. Writes are OPS-gated server-side.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './Toast';
import './AssetComponentsPanel.css';

const KIND_LABELS = {
  top: 'System slot (core)',
  robot: 'Fits the robot',
  krc: 'Fits the KRC controller',
  asset: 'Fits the system directly'
};

function ComponentTypeSettings() {
  const { authFetch } = useAuth();
  const { toastElement, showToast } = useToast();
  const [types, setTypes] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [newKind, setNewKind] = useState('robot');
  const [saving, setSaving] = useState(false);

  const fetchTypes = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/components/types');
      if (response.ok) setTypes((await response.json()).data || []);
    } catch (err) {
      console.error('Failed to fetch component types:', err);
    }
  }, [authFetch]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const addType = async () => {
    setSaving(true);
    try {
      const response = await authFetch('/api/v1/components/types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newLabel, parent_kind: newKind })
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Failed to add category');
      showToast(`${newLabel.trim()} added — parts can now be registered on the Components tab`, 'success');
      setNewLabel('');
      fetchTypes();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (type) => {
    try {
      const response = await authFetch(`/api/v1/components/types/${type.value}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !type.active })
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Failed to update category');
      fetchTypes();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div className="admin-section component-types-card">
      {toastElement}
      <div className="section-header">
        <div>
          <h2>Robot Component Categories</h2>
          <p className="section-desc">
            The part types tracked on each asset&apos;s Components tab. Add a category and say what
            it fits; existing parts keep their history if a category is retired.
          </p>
        </div>
      </div>

      <table className="component-types-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Fits</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {types.map(type => (
            <tr key={type.value}>
              <td>{type.label}</td>
              <td>{KIND_LABELS[type.parent_kind] || type.parent_kind}</td>
              <td>{type.active ? 'Active' : 'Retired'}</td>
              <td>
                {type.protected ? (
                  <span className="section-desc">Core</span>
                ) : (
                  <button type="button" className="btn-small" onClick={() => toggleActive(type)}>
                    {type.active ? 'Retire' : 'Reactivate'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="component-type-add">
        <input
          type="text"
          placeholder="New category, e.g. Air Regulator"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          maxLength={80}
        />
        <select value={newKind} onChange={e => setNewKind(e.target.value)}>
          <option value="robot">Fits the robot</option>
          <option value="krc">Fits the KRC controller</option>
          <option value="asset">Fits the system directly</option>
        </select>
        <button type="button" className="btn-primary" onClick={addType} disabled={saving || !newLabel.trim()}>
          {saving ? 'Adding...' : '+ Add category'}
        </button>
      </div>
    </div>
  );
}

export default ComponentTypeSettings;
